import PropTypes from 'prop-types';
import React from "react";
import htmlToFormattedText from "html-to-formatted-text";
import debounce from "lodash/debounce";
import keyBy from "lodash/keyBy";
import template from "lodash/template";
import mapValues from 'lodash/mapValues';

import { getLocalizedFormatDate, formatIsoDate } from 'mythil/time';
import { getClickHandler } from '../../ui/Table/helpers';
import { CalendarComponent, acceptedCalendarViews } from "./CalendarComponent";

import { useThemeColors } from "~/native-ui";
import parseISO from "date-fns/parseISO";


function getDateFromRawEvent(event, column) {
    const date = event?.[column]?.raw;
    return date ? parseISO(date.date ?? date) : null;
}

export class Calendar extends React.Component {
    state = {
        currentEvents: [],
        dateRange: { start: null, end: null }
    }

    eventsMap = {}
    start = new Date();

    calendarRef = React.createRef()

    static propTypes = {
        results: PropTypes.array.isRequired,
        calendarView: PropTypes.object.isRequired,
        view: PropTypes.oneOf(acceptedCalendarViews),
        onEventClick: PropTypes.func,
        onEventDoubleClick: PropTypes.func,
        onCalendarRangeChange: PropTypes.func,
        onViewChange: PropTypes.func,
        trans: PropTypes.func,
    }

    get titleColumn() {
        return this.props.calendarView?.title_column
    }

    get fromColumn() {
        return this.props.calendarView?.from_column
    }

    get toColumn() {
        return this.props.calendarView?.to_column
    }

    get locale() {
        return {
            code: this.props.trans("locale"),
            week: {
                dow: 1, // Monday is the first day of the week.
                doy: 4, // The week that contains Jan 4th is the first week of the year.
            },
            ...(this.props.trans("calendarLocale") || {}),
            moreLinkText: this.moreLinkText
        }
    }

    get calendarApi () {
        return this.calendarRef.current.getApi()
    }

    get firstDate() {
        const event = this.props.results?.find((event) => event?.[this.fromColumn]?.raw);
        return getDateFromRawEvent(event, this.fromColumn)
    }

    _template = null;
    get template() { return this._template }
    set template(_template) { this._template = template(_template);}

    constructor(props) {
        super(props);
        this.updateTitleTemplate();
    }

    componentDidMount() {
        const date = this.firstDate;
        this.setEventsMap();

        if (!date) {
            return;
        }

        this.calendarApi.gotoDate(new Date(date));
    }


    updateTitleTemplate(oldProps = {}) {
        if (oldProps.calendarView?.title_column !== this.titleColumn) {
            this.template = this.titleColumn;
        }
    }


    componentDidUpdate(oldProps) {
        const date = this.firstDate;

        this.updateTitleTemplate(oldProps)
        
        if (!date) {
            return
        }

        if (oldProps.results === this.props.results) {
            return;
        }

        this.setEventsMap();
        const { activeStart, activeEnd } = this.calendarApi.view;
        if (date >= activeStart && date <= activeEnd) {
            return
        }

        const oldDate = getDateFromRawEvent(oldProps.results?.find((event) => event?.[this.fromColumn]?.raw), this.fromColumn);
        if (this.areDatesEqual(oldDate, date)) {
            return;
        }
        
        this.calendarApi.gotoDate(new Date(date));
    }

    moreLinkContent(args) {
        return args.shortText
    }


    render() {
        let { view, results, calendarView } = this.props;
        const { default_view } = calendarView;
        view = acceptedCalendarViews.includes(view) ? view : default_view;

        return (
            <React.Fragment>
                <CalendarStyles />
                <CalendarComponent
                    ref={ this.calendarRef }
                    locales={[ this.locale ]}
                    locale={this.props.trans("locale")}
                    navLinks
                    weekNumbers
                    nowIndicator
                    height="100%"
                    dayMaxEvents={2}
                    dragScroll={false}
                    initialView={view}
                    scrollTime="00:00"
                    moreLinkContent={ this.moreLinkContent }
                    events={results}
                    
                    dayHeaderContent={this.renderHeaderContent} // custom render function
                    eventClick={this.handleEventClick}
                    eventsSet={this.handleEvents} // called after events are initialized/added/changed/removed
                    eventDataTransform={this.transformEventData}
                    datesSet={this.handleRangeUpdate}
                />
            </React.Fragment>
        )
    }

    moreLinkText = (count) => {
        return this.props.trans("", { count })
    }

    setEventsMap() {
        this.eventsMap = keyBy(this.props.results, "id.raw")
    }

    areDatesEqual(dateOne, dateTwo = this.firstDate) {
        return formatIsoDate(dateOne) === formatIsoDate(dateTwo);
    }


    changeDate = (date = this.firstDate) => {
        this.calendarApi.gotoDate(new Date(date));
    }

    renderHeaderContent = (obj) => {
        const { date } = obj;
        if (this.props.view === "month") {
            return getLocalizedFormatDate({
                ...this.props.trans("time")
            })(date, "dddd")
        }

        return getLocalizedFormatDate({
            ...this.props.trans("time")
        })(date, "ddd dd.mm")
    }


    handleRangeUpdate = debounce(({ end, start, view }) => {
        if (view.type !== this.props.view) {
            this.props.onViewChange?.(view.type);
        }

        if (!this.areDatesEqual(start, this.state.dateRange.start) || !this.areDatesEqual(end, this.state.dateRange.end)) {
            this.setState({ dateRange: { start, end } })
            this.props.onCalendarRangeChange({ from: start, to: end })
        }
    }, 200, { trailing: true })


    transformEventData = (event) => {
        const date = getDateFromRawEvent(event, this.fromColumn);
        if (!date) return false;

        const title = this.template && htmlToFormattedText(this.template(mapValues(event, (x) => x.rendered )))
        return {
            ...event,
            id:  event.id.raw,
            title: title || "",
            start: date,
            end: getDateFromRawEvent(event, this.toColumn),
        };
    }

    

    onClickEvent = ({ rowData }) => {
        this.props.onEventClick(rowData);
    }

    onDoubleClickEvent = ({ rowData }) => {
        this.props.onEventDoubleClick(rowData);
    }

    _handleEventClick = getClickHandler(
        (e) => this.onClickEvent(e),
        (e) => this.onDoubleClickEvent(e),
        200
    )

    handleEventClick = ({ event }) => {
        this._handleEventClick({ rowData: this.eventsMap[event.id] })
    };

    handleEvents = (events) => {
        this.setState({ currentEvents: events })
    }
}

const CalendarStyles = React.memo(() => {
    const { fontFamilyBody, fontFamilyHeading, primaryColor, textColor, primaryHover, textHover} = useThemeColors();

    const style = `
        .fc {
            font-family: ${fontFamilyBody};
            color: ${textColor};
            --fc-event-bg-color: ${primaryColor};
            --fc-event-border-color: ${primaryColor};


            --fc-button-text-color: ${textColor};
            --fc-button-hover-bg-color: ${textHover};

            --fc-button-bg-color: transparent;
            --fc-button-active-bg-color: transparent;
            --fc-button-border-color: transparent;
            --fc-button-active-border-color: transparent;
            --fc-button-hover-border-color: transparent;
        }

        .fc a {
            color: ${primaryColor};
        }

        .fc h2 {
            font-weight: normal;
            font-family: ${fontFamilyHeading};
        }

        .fc .fc-day {
            font-weight: normal;
        }

        .fc .fc-toolbar-chunk {
            align-items: center;
            display: flex;
        }

        .fc button[class*=today],
        .fc button[class*=prev],
        .fc button[class*=next] {
            --fc-button-text-color: ${primaryColor};
            --fc-button-hover-bg-color: ${primaryHover};
            
        }

        .fc button:disabled {
            --fc-button-bg-color: transparent;
            --fc-button-border-color: transparent;
            --fc-button-text-color: ${primaryColor};
            opacity: .4;
        }

        .fc button[class*=active] {
            --fc-button-text-color: ${primaryColor};
        }
    `;

    return (
        <style>{ style }</style>
    )
})